import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, FileText, User as UserIcon, Loader, Download, RefreshCw, Upload, CheckCircle } from 'lucide-react';

// Every /api/* call on this screen is behind requireQmsAccess on the server,
// so the JWT has to ride along. index.js already wraps window.fetch to attach
// it, but these calls set the header explicitly so the dependency is visible
// at the call site.
const authHeaders = () => {
  const token = localStorage.getItem('pmplus_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Anyone who can read the QMS documents can also add one: QMS users take
// training on new revisions before anyone else does. This is the same
// condition App.js uses to show the QMS Assistant at all, and the same set
// requireQmsAccess admits on the server, which is the real gate - this only
// decides whether the tab is shown. Keep the three in step.
const canUploadDocs = (u) =>
  !!u && (u.user_role === 'admin' || u.isAdmin === 'Y' || u.user_role === 'qms_user');

const QmpAssistant = ({ user }) => {
  const canUpload = canUploadDocs(user);
  const [activeTab, setActiveTab] = useState('chat');
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content:
        "Hi. I'm the QMS Assistant. Ask me anything about the quality procedures, work instructions, or the quality manual. For example: \"What is the CAPA process?\" or \"How are internal audits scheduled?\"",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const scrollRef = useRef(null);

  // Documents tab
  const [files, setFiles] = useState([]);
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState('');
  const [downloading, setDownloading] = useState(null);

  // Upload tab (id 1 only)
  const [pendingFile, setPendingFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const loadFiles = useCallback(async () => {
    setFilesLoading(true);
    setFilesError('');
    try {
      const res = await fetch('/api/qmp/files', { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'request failed');
      setFiles(Array.isArray(data.files) ? data.files : []);
      setFilesLoaded(true);
    } catch (err) {
      setFilesError(`Could not load the document list: ${err.message}`);
    }
    setFilesLoading(false);
  }, []);

  // Fetch once, the first time the tab is opened.
  useEffect(() => {
    if (activeTab === 'documents' && !filesLoaded && !filesLoading) {
      loadFiles();
    }
  }, [activeTab, filesLoaded, filesLoading, loadFiles]);

  // The download route needs the Authorization header, so a plain <a href>
  // would come back 401. Fetch it, then hand the blob to a throwaway anchor.
  const downloadFile = async (filename) => {
    setDownloading(filename);
    setFilesError('');
    let objectUrl = null;
    try {
      const res = await fetch(`/api/qmp/download?file=${encodeURIComponent(filename)}`, {
        headers: authHeaders(),
      });
      if (!res.ok) {
        let msg = 'request failed';
        try {
          const data = await res.json();
          msg = data.error || msg;
        } catch (_) {
          // non-JSON error body — keep the generic message
        }
        throw new Error(msg);
      }
      const blob = await res.blob();
      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setFilesError(`Could not download "${filename}": ${err.message}`);
    }
    // Revoke on a delay — Safari can abort the save if the URL dies too soon.
    if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    setDownloading(null);
  };

  const pickFile = (file) => {
    setUploadError('');
    setUploadResult(null);
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) {
      setPendingFile(null);
      setUploadError('Only .pdf and .docx files can be uploaded.');
      return;
    }
    setPendingFile(file);
  };

  // XHR rather than fetch: fetch cannot report upload progress, and a 3MB
  // procedure over a slow connection needs a bar. index.js only wraps
  // window.fetch with the token, so set the header here.
  const uploadDocument = () => {
    if (!pendingFile || uploading) return;
    setUploading(true);
    setUploadPct(0);
    setUploadError('');
    setUploadResult(null);

    const form = new FormData();
    form.append('file', pendingFile);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/qmp/upload');
    const token = localStorage.getItem('pmplus_token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setUploadPct(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch (_) {
        // non-JSON body — fall through to the status-code message
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        setUploadResult(data);
        setPendingFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        // The document list has changed underneath the Documents tab.
        loadFiles();
      } else {
        setUploadError(data.error || `Upload failed (${xhr.status}).`);
      }
      setUploading(false);
    };

    xhr.onerror = () => {
      setUploadError('Connection failed. Please try again.');
      setUploading(false);
    };

    xhr.send(form);
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/qmp/chat/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        // Identity comes from the JWT — the server ignores any userId sent here.
        body: JSON.stringify({
          message: text,
          conversationId,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: `Sorry, something went wrong: ${data.error || 'request failed'}.` },
        ]);
      } else {
        if (data.conversationId) setConversationId(data.conversationId);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', content: data.reply || '(no answer returned)' },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Connection failed. Please try again.' },
      ]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="content-card" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)', padding: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FileText size={20} color="white" />
        </div>
        <div>
          <div style={{ fontWeight: 600, color: '#111827' }}>QMS Assistant</div>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>Answers from your quality procedures and work instructions</div>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem' }}>
          {[
            { key: 'chat', label: 'Chat' },
            { key: 'documents', label: 'Documents' },
            ...(canUpload ? [{ key: 'upload', label: 'Upload' }] : []),
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                border: '1px solid ' + (activeTab === t.key ? '#2563eb' : '#d1d5db'),
                background: activeTab === t.key ? '#2563eb' : '#fff',
                color: activeTab === t.key ? '#fff' : '#374151',
                borderRadius: 8,
                padding: '0.4rem 0.9rem',
                fontSize: '0.85rem',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'upload' && canUpload && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '1rem', lineHeight: 1.5 }}>
            Add a document, or replace one with a newer revision. The document number in the
            filename decides which: a number already in the library has its old chunks and its
            old file replaced, a number that is new is simply added. Name files like
            <span style={{ color: '#374151', fontWeight: 500 }}> QMP-7.5.3-WI07 Inventory Process Rev 04.docx</span>.
          </div>

          <div
            onClick={() => { if (!uploading && fileInputRef.current) fileInputRef.current.click(); }}
            onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (uploading) return;
              pickFile(e.dataTransfer.files && e.dataTransfer.files[0]);
            }}
            style={{
              border: '2px dashed ' + (dragOver ? '#2563eb' : '#d1d5db'),
              background: dragOver ? '#eff6ff' : '#fafafa',
              borderRadius: 12,
              padding: '2rem 1rem',
              textAlign: 'center',
              cursor: uploading ? 'default' : 'pointer',
            }}
          >
            <Upload size={26} color="#2563eb" />
            <div style={{ marginTop: '0.6rem', fontSize: '0.92rem', color: '#111827', fontWeight: pendingFile ? 600 : 400 }}>
              {pendingFile ? pendingFile.name : 'Drop a .pdf or .docx here, or click to choose a file'}
            </div>
            {pendingFile && (
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                {(pendingFile.size / 1024).toFixed(0)} KB
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx"
              style={{ display: 'none' }}
              onChange={(e) => pickFile(e.target.files && e.target.files[0])}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
            <button
              onClick={uploadDocument}
              disabled={!pendingFile || uploading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                background: !pendingFile || uploading ? '#9ca3af' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '0.5rem 1rem',
                fontSize: '0.88rem',
                cursor: !pendingFile || uploading ? 'default' : 'pointer',
              }}
            >
              {uploading ? <Loader size={15} className="qmp-spin" /> : <Upload size={15} />}
              {uploading ? (uploadPct < 100 ? `Uploading ${uploadPct}%` : 'Indexing') : 'Upload'}
            </button>
            {pendingFile && !uploading && (
              <button
                onClick={() => { setPendingFile(null); setUploadError(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                style={{ border: '1px solid #d1d5db', background: '#fff', color: '#374151', borderRadius: 8, padding: '0.5rem 0.9rem', fontSize: '0.88rem', cursor: 'pointer' }}
              >
                Clear
              </button>
            )}
          </div>

          {uploading && (
            <div style={{ marginTop: '0.85rem' }}>
              <div style={{ height: 6, background: '#e5e7eb', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${uploadPct}%`, background: '#2563eb', transition: 'width 0.2s' }} />
              </div>
              <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.4rem' }}>
                {uploadPct < 100
                  ? 'Sending the file...'
                  : 'Reading, chunking and embedding the document. This takes a few seconds.'}
              </div>
            </div>
          )}

          {uploadError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem', marginTop: '1rem' }}>
              {uploadError}
            </div>
          )}

          {uploadResult && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '0.9rem 1rem', marginTop: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534', fontWeight: 600, fontSize: '0.92rem' }}>
                <CheckCircle size={17} />
                {uploadResult.chunksRemoved > 0 ? 'Document replaced' : 'Document added'}
              </div>
              <div style={{ fontSize: '0.86rem', color: '#374151', marginTop: '0.5rem', lineHeight: 1.6 }}>
                <div>
                  <strong>{uploadResult.docNumber}</strong>
                  {uploadResult.title ? ` — ${uploadResult.title}` : ''}
                  {uploadResult.revision ? ` (Rev ${uploadResult.revision})` : ''}
                </div>
                <div>
                  {uploadResult.chunksRemoved} chunk{uploadResult.chunksRemoved === 1 ? '' : 's'} replaced,{' '}
                  {uploadResult.chunksAdded} chunk{uploadResult.chunksAdded === 1 ? '' : 's'} added.
                </div>
                {Array.isArray(uploadResult.filesRemoved) && uploadResult.filesRemoved.length > 0 && (
                  <div style={{ color: '#6b7280' }}>
                    Superseded file removed: {uploadResult.filesRemoved.join(', ')}
                  </div>
                )}
                <div style={{ color: '#6b7280' }}>The assistant answers from this revision now.</div>
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
              {filesLoading
                ? 'Loading documents...'
                : `${files.length} document${files.length === 1 ? '' : 's'} available`}
            </div>
            <button
              onClick={loadFiles}
              disabled={filesLoading}
              style={{
                marginLeft: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                border: '1px solid #d1d5db',
                background: '#fff',
                color: '#374151',
                borderRadius: 8,
                padding: '0.4rem 0.8rem',
                fontSize: '0.85rem',
                cursor: filesLoading ? 'default' : 'pointer',
              }}
            >
              <RefreshCw size={14} className={filesLoading ? 'qmp-spin' : undefined} />
              Refresh
            </button>
          </div>

          {filesError && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.9rem', marginBottom: '1rem' }}>
              {filesError}
            </div>
          )}

          {!filesLoading && !filesError && files.length === 0 && (
            <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>No documents found.</div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {files.map((f) => (
              <div
                key={f.filename}
                style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.75rem 1rem', background: '#fff' }}
              >
                <div style={{ flexShrink: 0, width: 34, height: 34, borderRadius: 8, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileText size={18} color="#2563eb" />
                </div>

                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, color: '#111827', fontSize: '0.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {f.docNumber ? `${f.docNumber} — ${f.title}` : f.title}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: 2 }}>
                    {f.revision ? `Rev ${f.revision}` : 'No revision'}
                    {f.ext ? ` · ${f.ext.toUpperCase()}` : ''}
                  </div>
                </div>

                <button
                  onClick={() => downloadFile(f.filename)}
                  disabled={downloading === f.filename}
                  style={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    background: downloading === f.filename ? '#9ca3af' : '#2563eb',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    padding: '0.45rem 0.9rem',
                    fontSize: '0.85rem',
                    cursor: downloading === f.filename ? 'default' : 'pointer',
                  }}
                >
                  {downloading === f.filename ? <Loader size={15} className="qmp-spin" /> : <Download size={15} />}
                  {downloading === f.filename ? 'Downloading' : 'Download'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'chat' && (
      <>

      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
            <div style={{ flexShrink: 0, width: 32, height: 32, borderRadius: 8, background: m.role === 'user' ? '#374151' : '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {m.role === 'user' ? <UserIcon size={18} color="white" /> : <FileText size={18} color="white" />}
            </div>
            <div style={{ maxWidth: '78%', background: m.role === 'user' ? '#374151' : '#f3f4f6', color: m.role === 'user' ? 'white' : '#111827', padding: '0.75rem 1rem', borderRadius: 12, whiteSpace: 'pre-wrap', lineHeight: 1.5, fontSize: '0.95rem' }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileText size={18} color="white" />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#6b7280' }}>
              <Loader size={16} className="qmp-spin" />
              Searching the QMS documents...
            </div>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.75rem 1rem', display: 'flex', gap: '0.5rem', background: '#fff' }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about a procedure, work instruction, or form..."
          rows={1}
          style={{ flex: 1, resize: 'none', border: '1px solid #d1d5db', borderRadius: 8, padding: '0.6rem 0.85rem', fontSize: '0.95rem', fontFamily: 'inherit', outline: 'none', maxHeight: 120 }}
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          style={{ background: loading || !input.trim() ? '#9ca3af' : '#2563eb', color: 'white', border: 'none', borderRadius: 8, padding: '0 1rem', cursor: loading || !input.trim() ? 'default' : 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <Send size={18} />
        </button>
      </div>
      </>
      )}

      <style>{`.qmp-spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
};

export default QmpAssistant;
